// ─────────────────────────────────────────────────────────────────────────────
// control_bridge.ts — Remote control bridge for PWA mode
//
// Connects to the run_pwa.py control server over long-polling HTTP.  The
// server queues commands from external clients (Claude skill, curl, etc.);
// this bridge drains the queue, executes them against the live
// StandaloneHost + CanvasApp, and posts results back.
//
// It also pushes events (action dispatched, page changed, …) to the server
// so external clients can poll /api/changes for incremental updates.
//
// Exposed as `window.grimoire` for direct in-browser access (Playwright,
// Chrome DevTools, dev console).
// ─────────────────────────────────────────────────────────────────────────────

import type { StandaloneHost } from './standalone_host';
import type { GrimoireAction } from '@shared/grimoire_action';
import type { InkStyle, StrokePoint, PenStyle } from '@shared/notebook_model';
import { DEFAULT_INK_STYLE } from '@shared/notebook_model';

type App = {
  _canvas: HTMLCanvasElement;
  onHostMessage(msg: unknown): void;
};

interface Command {
  id: string;
  method: string;
  params?: Record<string, unknown>;
}

const NEXT_PATH = '/api/_next';
const REPLY_PATH = '/api/_reply';
const EVENT_PATH = '/api/_event';
const HELLO_PATH = '/api/_hello';

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

function newId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Count leaf (non-container) nodes in a /api/tree response. Tolerates the
 *  "no tree available" case by returning 0. */
function countTreePages(node: unknown): number {
  if (!node || typeof node !== 'object') return 0;
  const n = node as { isContainer?: boolean; children?: unknown[] };
  const here = n.isContainer === false ? 1 : 0;
  if (!Array.isArray(n.children)) return here;
  return here + n.children.reduce<number>((sum, c) => sum + countTreePages(c), 0);
}

// ── Convenience action builders ──────────────────────────────────────────────
// Shared between external callers and the bridge itself.  All dispatched
// actions carry origin='api' so they're distinguishable in the action log.

function ts(): number { return Date.now(); }

function makeRectVertices(bbox: { x: number; y: number; width: number; height: number }) {
  const { x, y, width: w, height: h } = bbox;
  return [
    { id: 'v0', x: x,     y: y },
    { id: 'v1', x: x + w, y: y },
    { id: 'v2', x: x + w, y: y + h },
    { id: 'v3', x: x,     y: y + h },
  ];
}
function makeRectEdges() {
  return [
    { id: 'e0', from: 'v0', to: 'v1', type: 'line' },
    { id: 'e1', from: 'v1', to: 'v2', type: 'line' },
    { id: 'e2', from: 'v2', to: 'v3', type: 'line' },
    { id: 'e3', from: 'v3', to: 'v0', type: 'line' },
  ];
}

// ── Point/stroke utilities ───────────────────────────────────────────────────

interface XY { x: number; y: number }

function bboxOf(points: ReadonlyArray<XY>): { x: number; y: number; width: number; height: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Resample a point list to exactly M points by uniform index subsampling.
 *  Preserves first/last, cheap, good enough for agent-facing shape fidelity.
 *  If the input is already shorter than M, returns it unchanged. */
function resamplePoints(points: ReadonlyArray<XY>, M: number): XY[] {
  if (points.length <= M) return points.map(p => ({ x: p.x, y: p.y }));
  const out: XY[] = new Array(M);
  for (let i = 0; i < M; i++) {
    const idx = Math.round((i * (points.length - 1)) / (M - 1));
    out[i] = { x: points[idx].x, y: points[idx].y };
  }
  return out;
}

/** Build Catmull–Rom cubic Bezier control points for a smooth spline through
 *  N waypoints.  Returns one edge per segment with cp1/cp2 in controlPoints. */
function splineEdges(waypoints: ReadonlyArray<XY>): Array<{
  id: string; from: string; to: string; type: 'curve';
  controlPoints: [XY, XY];
}> {
  const edges = [];
  const n = waypoints.length;
  for (let i = 0; i < n - 1; i++) {
    const p0 = i > 0 ? waypoints[i - 1] : waypoints[i];
    const p1 = waypoints[i];
    const p2 = waypoints[i + 1];
    const p3 = i + 2 < n ? waypoints[i + 2] : waypoints[i + 1];
    const cp1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const cp2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    edges.push({
      id: `e${i}`,
      from: `v${i}`,
      to: `v${i + 1}`,
      type: 'curve' as const,
      controlPoints: [cp1, cp2] as [XY, XY],
    });
  }
  return edges;
}

function makeInkStyle(opts: {
  pen?: PenStyle;
  color?: string;
  size?: number;
  opacity?: number;
  style?: Partial<InkStyle>;
}): InkStyle {
  const base: InkStyle = { ...DEFAULT_INK_STYLE };
  if (opts.pen) base.style = opts.pen;
  if (opts.color) base.color = opts.color;
  if (opts.size != null) base.size = opts.size;
  if (opts.opacity != null) base.opacity = opts.opacity;
  return { ...base, ...(opts.style ?? {}) };
}

// ── LaTeX detection ─────────────────────────────────────────────────────────
//
// Heuristic pattern matcher for math-ish text. Matches:
//   • $…$ and $$…$$ delimiter blocks
//   • common LaTeX commands: \frac \int \sum \prod \sqrt \pm \cdot \times
//     \div \leq \geq \neq \approx \equiv \infty \partial \nabla \Delta
//     \alpha..\omega \Alpha..\Omega and similar single-token commands
// False positives are acceptable — callers opt in via autoDetectLatex.
const LATEX_COMMANDS = [
  'frac', 'int', 'iint', 'iiint', 'oint', 'sum', 'prod', 'lim', 'log', 'ln', 'exp',
  'sqrt', 'cdot', 'times', 'div', 'pm', 'mp', 'leq', 'geq', 'neq', 'approx',
  'equiv', 'sim', 'cong', 'infty', 'partial', 'nabla', 'forall', 'exists',
  'in', 'notin', 'subset', 'supset', 'cup', 'cap', 'wedge', 'vee',
  'Rightarrow', 'Leftarrow', 'Leftrightarrow', 'to', 'mapsto',
  'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'varepsilon', 'zeta', 'eta',
  'theta', 'vartheta', 'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'pi', 'varpi',
  'rho', 'varrho', 'sigma', 'varsigma', 'tau', 'upsilon', 'phi', 'varphi', 'chi',
  'psi', 'omega', 'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta',
  'Theta', 'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Pi', 'Rho', 'Sigma',
  'Tau', 'Upsilon', 'Phi', 'Chi', 'Psi', 'Omega',
  'mathbb', 'mathcal', 'mathbf', 'text', 'begin', 'end', 'left', 'right',
  'frac', 'binom', 'overline', 'underline', 'hat', 'tilde', 'vec', 'bar',
];
const LATEX_CMD_RE = new RegExp('\\\\(' + LATEX_COMMANDS.join('|') + ')(?:[^a-zA-Z]|$)');
const LATEX_DELIM_RE = /(\$\$[\s\S]+?\$\$|\$[^\$\n]+\$)/;

/** Returns true if the text looks LaTeX-y — has $-delimited math or a
 *  known LaTeX command. Caller decides how to act on it. */
function looksLikeLatex(content: string): boolean {
  if (!content) return false;
  if (LATEX_DELIM_RE.test(content)) return true;
  if (LATEX_CMD_RE.test(content)) return true;
  return false;
}

function toStrokePoints(points: ReadonlyArray<Partial<StrokePoint> & XY>, tStart: number): StrokePoint[] {
  return points.map((p, i) => ({
    x: p.x, y: p.y,
    pressure: p.pressure ?? 0.5,
    tiltX: p.tiltX ?? 0,
    tiltY: p.tiltY ?? 0,
    twist: p.twist ?? 0,
    timestamp: p.timestamp ?? (tStart + i),
  }));
}

// ── Bridge ────────────────────────────────────────────────────────────────────

// ── Group registry ───────────────────────────────────────────────────────────
// Groups are bridge-side metadata: a named bag of block/stroke ids on a page.
// Not stored in the action log; lost on reload. Operations (move/copy/layer)
// fan out to the appropriate per-element actions.

interface GroupEntry {
  id: string;
  pageId: string;
  blockIds: Set<string>;
  strokeIds: Set<string>;
  createdAt: number;
}

export class ControlBridge {
  private _host: StandaloneHost;
  private _getApp: () => App | null;
  private _running = false;
  private _abort: AbortController | null = null;
  private _eventSeq = 0;
  private _groups = new Map<string, GroupEntry>();

  // Journal snapshot (populated on start() from /api/journal + /api/tree).
  // Null while server hasn't responded or when running without a server.
  private _journalInfo: Record<string, unknown> | null = null;
  private _journalTree: Record<string, unknown> | null = null;
  private _journalFetchedAt = 0;

  constructor(host: StandaloneHost, getApp: () => App | null) {
    this._host = host;
    this._getApp = getApp;
  }

  async start(): Promise<void> {
    if (this._running) return;
    this._running = true;
    // Announce ourselves so the server can log and clear stale state.
    try {
      await fetch(HELLO_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectedAt: ts(), href: location.href }),
      });
    } catch { /* server might not have control endpoints — bridge still works for window.grimoire */ }
    // Fire-and-forget: pull the journal tree so window.grimoire.getVaultTree()
    // is populated before external tools start poking. Failures are silent —
    // old servers may not have /api/journal at all.
    this._refreshJournal().catch(() => { /* no journal server — fine */ });
    this._installEventTap();
    this._pollLoop();
  }

  private async _refreshJournal(): Promise<void> {
    try {
      const [journalRes, treeRes] = await Promise.all([
        fetch('/api/journal'),
        fetch('/api/tree'),
      ]);
      if (journalRes.ok) this._journalInfo = await journalRes.json() as Record<string, unknown>;
      if (treeRes.ok) this._journalTree = await treeRes.json() as Record<string, unknown>;
      this._journalFetchedAt = ts();
      if (this._journalInfo) {
        const pageCount = countTreePages(this._journalTree);
        console.log(
          `[journal] ${this._journalInfo.root} — ${pageCount} page(s) via ${
            (this._journalInfo.drivers as Array<{ name: string }> | undefined)
              ?.map(d => d.name).join(', ') ?? 'no drivers'
          }`,
        );
      }
    } catch {
      // Server unreachable or no /api/journal endpoint — bridge stays in
      // IDB-only mode. Explicit refresh via api.refreshVault() can retry.
    }
  }

  stop(): void {
    this._running = false;
    this._abort?.abort();
  }

  // ── Long-poll loop ─────────────────────────────────────────────────────────

  private async _pollLoop(): Promise<void> {
    let backoff = 500;
    while (this._running) {
      this._abort = new AbortController();
      try {
        const res = await fetch(NEXT_PATH, { signal: this._abort.signal });
        if (res.status === 204) { backoff = 500; continue; }
        if (!res.ok) { await sleep(backoff); backoff = Math.min(backoff * 2, 10_000); continue; }
        backoff = 500;
        const cmd = await res.json() as Command;
        this._handle(cmd).then(result => {
          return fetch(REPLY_PATH + '?id=' + encodeURIComponent(cmd.id), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(result),
          });
        }).catch(err => console.warn('[grimoire-bridge] reply failed', err));
      } catch (e) {
        if ((e as Error).name === 'AbortError') continue;
        await sleep(backoff);
        backoff = Math.min(backoff * 2, 10_000);
      }
    }
  }

  private async _handle(cmd: Command): Promise<{ ok: true; result: unknown } | { ok: false; error: string }> {
    const fn = (this.api as unknown as Record<string, (p: Record<string, unknown>) => unknown>)[cmd.method];
    if (typeof fn !== 'function') {
      return { ok: false, error: 'unknown method: ' + cmd.method };
    }
    try {
      const result = await fn.call(this.api, cmd.params ?? {});
      return { ok: true, result };
    } catch (e) {
      return { ok: false, error: String((e as Error)?.message ?? e) };
    }
  }

  // ── Event push (action dispatched → server) ────────────────────────────────

  private _installEventTap(): void {
    const host = this._host as unknown as {
      _applyBlockAction(a: GrimoireAction): void;
      _onWebviewMessage(m: { type: string; payload?: unknown }): void;
    };
    // Wrap _applyBlockAction so EVERY dispatched action (gesture, agent,
    // api, …) produces an event.  Applied once.
    const tag = '__grimoire_bridge_tapped';
    if ((host as unknown as Record<string, unknown>)[tag]) return;
    (host as unknown as Record<string, unknown>)[tag] = true;
    const orig = host._applyBlockAction.bind(host);
    host._applyBlockAction = (action: GrimoireAction) => {
      orig(action);
      try {
        const a = action as unknown as Record<string, unknown>;
        this._pushEvent('action', {
          type: action.type,
          pageId: a.pageId,
          blockId: a.blockId,
          origin: a.origin,
          timestamp: action.timestamp,
        });
      } catch { /* swallow — event push must never affect app logic */ }
    };
  }

  private _pushEvent(type: string, data: Record<string, unknown>): void {
    const seq = ++this._eventSeq;
    fetch(EVENT_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ seq, type, data, timestamp: ts() }),
    }).catch(() => { /* server offline — fine */ });
  }

  // ── Public API (remote methods + window.grimoire) ──────────────────────────
  //
  // All methods take a single params object and return a plain JSON value.
  // These are the verbs the Claude skill / external tooling can call.

  api = {
    // ── Read ─────────────────────────────────────────────────────────────────

    health: async (): Promise<{ ok: true; currentPageId: string | undefined; pageCount: number }> => {
      const h = this._host as unknown as { _pages: Array<{ id: string }>; _currentPageIndex: number; _currentPageId?: string };
      return {
        ok: true,
        currentPageId: h._currentPageId ?? h._pages[h._currentPageIndex]?.id,
        pageCount: h._pages.length,
      };
    },

    getState: async () => {
      const h = this._host as unknown as { _serialiseNotebook(): unknown };
      return h._serialiseNotebook();
    },

    listPages: async () => {
      const h = this._host as unknown as { _pages: Array<Record<string, unknown>> };
      return h._pages.map(p => ({
        id: p.id,
        title: p.title,
        index: p.index,
        sectionId: p.sectionId,
        isSectionHeader: p.isSectionHeader,
        color: p.color,
        blockCount: (p.blocks as Map<string, unknown>).size,
        updatedAt: p.updatedAt,
      }));
    },

    // Journal metadata: root, drivers, reserved dirs, level labels.
    // Null if running without a /api server (direct-open index.html).
    getJournal: async () => ({
      info: this._journalInfo,
      fetchedAt: this._journalFetchedAt || null,
    }),

    // Tree of PageNodes under the journal root (folders = collections/chapters/
    // sections, files = pages). Cached from startup; pass {refresh:true} to
    // re-scan from the server.
    getJournalTree: async ({ refresh }: { refresh?: boolean } = {}) => {
      if (refresh || !this._journalTree) await this._refreshJournal();
      return {
        tree: this._journalTree,
        fetchedAt: this._journalFetchedAt || null,
      };
    },

    refreshJournal: async () => {
      await this._refreshJournal();
      return {
        info: this._journalInfo,
        tree: this._journalTree,
        fetchedAt: this._journalFetchedAt || null,
      };
    },

    // Legacy aliases — kept so documentation / scripts that call
    // window.grimoire.getVault*() still work during the rename transition.
    getVault: async () => ({
      info: this._journalInfo,
      fetchedAt: this._journalFetchedAt || null,
    }),
    getVaultTree: async ({ refresh }: { refresh?: boolean } = {}) => {
      if (refresh || !this._journalTree) await this._refreshJournal();
      return {
        tree: this._journalTree,
        fetchedAt: this._journalFetchedAt || null,
      };
    },
    refreshVault: async () => {
      await this._refreshJournal();
      return {
        info: this._journalInfo,
        tree: this._journalTree,
        fetchedAt: this._journalFetchedAt || null,
      };
    },

    // Journal-wise session state (persisted to <root>/.ox/session.json on the
    // server). Unlike IDB state, it travels with the folder and has no global
    // "last-opened" concept.
    getSession: async (): Promise<Record<string, unknown>> => {
      try {
        const res = await fetch('/api/session');
        if (!res.ok) return {};
        return await res.json() as Record<string, unknown>;
      } catch {
        return {};
      }
    },

    saveSession: async (session: Record<string, unknown>): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await fetch('/api/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(session ?? {}),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
          return { ok: false, error: (body as { error?: string }).error ?? 'HTTP ' + res.status };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, error: String((e as Error)?.message ?? e) };
      }
    },

    // Driver-backed page IO. `id` is the forward-slash relative path from
    // the journal root — same shape /api/tree hands out.
    readPage: async ({ id }: { id: string }): Promise<Record<string, unknown>> => {
      const res = await fetch('/api/page/read?id=' + encodeURIComponent(id));
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
        throw new Error((body as { error?: string }).error ?? 'HTTP ' + res.status);
      }
      return await res.json() as Record<string, unknown>;
    },

    savePage: async (
      { id, blocks, strokes, meta, create }: {
        id: string;
        blocks?: unknown[];
        strokes?: unknown[];
        meta?: Record<string, unknown>;
        create?: boolean;
      },
    ): Promise<Record<string, unknown>> => {
      const res = await fetch('/api/page/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          blocks: blocks ?? [],
          strokes: strokes ?? [],
          meta: meta ?? {},
          create: create === true,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'HTTP ' + res.status }));
        throw new Error((body as { error?: string }).error ?? 'HTTP ' + res.status);
      }
      return await res.json() as Record<string, unknown>;
    },

    getPage: async ({ pageId, strokes, strokeSamples }: {
      pageId: string;
      /** 'none' (default) | 'polyline' | 'knots'. */
      strokes?: 'none' | 'polyline' | 'knots';
      /** Points per stroke when strokes='polyline'. Default 16. */
      strokeSamples?: number;
    }) => {
      const h = this._host as unknown as {
        _pages: Array<Record<string, unknown>>;
        _serialiseBlock(b: Record<string, unknown>): Record<string, unknown>;
        _pageStrokes: Map<string, Map<string, {
          id: string;
          points: StrokePoint[];
          style: unknown;
          timestamp: number;
          pageLayer?: number;
        }>>;
      };
      const page = h._pages.find(p => p.id === pageId);
      if (!page) throw new Error('page not found: ' + pageId);
      const blocks = page.blocks as Map<string, Record<string, unknown>>;
      const arrows = page.arrows as Map<string, Record<string, unknown>>;

      const result: Record<string, unknown> = {
        id: page.id,
        title: page.title,
        index: page.index,
        sectionId: page.sectionId,
        isSectionHeader: page.isSectionHeader,
        color: page.color,
        blocks: [...blocks.values()].map(b => h._serialiseBlock(b)),
        arrows: [...arrows.values()].map(a => ({
          id: a.id, fromId: a.fromId, toId: a.toId, type: a.type, confirmed: a.confirmed,
        })),
      };

      const mode = strokes ?? 'none';
      if (mode !== 'none') {
        const strokeMap = h._pageStrokes.get(pageId);
        const list = strokeMap ? [...strokeMap.values()] : [];
        if (mode === 'polyline') {
          const M = Math.max(2, Math.min(256, strokeSamples ?? 16));
          result.strokes = list.map(s => ({
            id: s.id,
            pageLayer: s.pageLayer,
            bbox: bboxOf(s.points),
            points: resamplePoints(s.points, M),
          }));
        } else { // knots
          result.strokes = list.map(s => ({
            id: s.id,
            pageLayer: s.pageLayer,
            style: s.style,
            bbox: bboxOf(s.points),
            points: s.points.map(p => ({
              x: p.x, y: p.y,
              pressure: p.pressure,
              tiltX: p.tiltX,
              tiltY: p.tiltY,
              twist: p.twist,
              t: p.timestamp,
            })),
          }));
        }
      }
      return result;
    },

    currentPage: async () => {
      const h = this._host as unknown as { _pages: Array<{ id: string }>; _currentPageIndex: number; _currentPageId?: string };
      return { id: h._currentPageId ?? h._pages[h._currentPageIndex]?.id };
    },

    screenshot: async ({ format }: { format?: 'dataurl' | 'base64' } = {}) => {
      const app = this._getApp();
      if (!app) throw new Error('canvas app not ready');
      const dataUrl = app._canvas.toDataURL('image/png');
      if (format === 'base64') {
        const comma = dataUrl.indexOf(',');
        return { mime: 'image/png', base64: dataUrl.slice(comma + 1) };
      }
      return { mime: 'image/png', dataUrl };
    },

    // ── Dispatch (low-level escape hatch) ───────────────────────────────────

    dispatch: async ({ action }: { action: GrimoireAction }) => {
      this._dispatch(action);
      return { dispatched: true, type: action.type };
    },

    // ── Write: text ─────────────────────────────────────────────────────────

    createTextBlock: async ({ pageId, bbox, content, pageLayer,
      fontSize, fontFamily, color, bold, italic, underline, agentId, groupId,
      autoDetectLatex, frame }: {
      pageId: string;
      bbox: { x: number; y: number; width: number; height: number };
      content: string;
      pageLayer?: number;
      /** Default 16. Required for the renderer's OCR-path (undefined breaks font metrics). */
      fontSize?: number;
      fontFamily?: string;
      color?: string;
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      /** 'ireth' or 'laura' — triggers cursive-on-parchment rendering. */
      agentId?: 'ireth' | 'laura';
      /** Optional: add the new block to an existing bridge-side group. */
      groupId?: string;
      /** If true and the content contains LaTeX delimiters or commands,
       *  route to createLatexBlock instead.  Default false. */
      autoDetectLatex?: boolean;
      /** Hand-drawn sketch rectangle around the text. Default true.
       *  Set false for frameless OCR text (headers, captions, annotations). */
      frame?: boolean;
    }) => {
      if (autoDetectLatex && looksLikeLatex(content)) {
        const latexResult = await (this.api as unknown as {
          createLatexBlock: (p: {
            pageId: string;
            bbox: { x: number; y: number; width: number; height: number };
            latex: string; pageLayer?: number; groupId?: string;
          }) => Promise<{ blockId: string }>;
        }).createLatexBlock({ pageId, bbox, latex: content, pageLayer, groupId });
        return { blockId: latexResult.blockId, routedToLatex: true };
      }
      const blockId = `api_text_${newId()}`;
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.create', blockId, pageId, kind: 'text', bbox,
        pageLayer, origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      if (content) {
        this._dispatch({
          type: 'text.update', blockId, pageId, content,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
      // Always emit text.style — the normal-text renderer uses block.fontSize
      // directly (no fallback), so leaving it undefined produces `undefinedpx`
      // in the font string and the glyphs never paint.
      const style: Record<string, unknown> = { fontSize: fontSize ?? 16 };
      if (fontFamily) style.fontFamily = fontFamily;
      if (color) style.color = color;
      if (bold) style.bold = true;
      if (italic) style.italic = true;
      if (underline) style.underline = true;
      if (agentId) style.agentId = agentId;
      if (frame === false) style.showFrame = false;
      this._dispatch({
        type: 'text.style', blockId, pageId, style,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      if (groupId) this._addBlockToGroup(groupId, pageId, blockId);
      return { blockId };
    },

    // ── Write: LaTeX (math) blocks ──────────────────────────────────────────
    //
    // NOTE ON RENDERING: the PWA's block renderer does not typeset LaTeX — it
    // displays the source (with a gold confirmation badge and sepia overlay).
    // For true math rendering, KaTeX/MathJax would need to be bundled.  This
    // API still preserves the semantic distinction (latex block vs text block)
    // which matters for downstream tools, action log replay, and future
    // rendering upgrades.

    createLatexBlock: async ({ pageId, bbox, latex, pageLayer, groupId }: {
      pageId: string;
      bbox: { x: number; y: number; width: number; height: number };
      /** LaTeX source, e.g. '\\int_0^1 f(x)\\,dx' or '$a^2+b^2=c^2$'. */
      latex: string;
      pageLayer?: number;
      groupId?: string;
    }) => {
      if (!latex) throw new Error('createLatexBlock: latex source required');
      const blockId = `api_latex_${newId()}`;
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.create', blockId, pageId, kind: 'latex', bbox,
        pageLayer, origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      // Skip straight to confirm — the API doesn't need the suggestion
      // ghost that gesture-recognized input goes through.
      this._dispatch({
        type: 'latex.confirm', blockId, pageId, latex,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      if (groupId) this._addBlockToGroup(groupId, pageId, blockId);
      return { blockId };
    },

    updateText: async ({ pageId, blockId, content }: {
      pageId: string; blockId: string; content: string;
    }) => {
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'text.update', blockId, pageId, content,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { ok: true };
    },

    // ── Write: blocks (common ops) ──────────────────────────────────────────

    moveBlock: async ({ pageId, blockId, dx, dy }: {
      pageId: string; blockId: string; dx: number; dy: number;
    }) => {
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.move', blockId, pageId, dx, dy,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { ok: true };
    },

    deleteBlock: async ({ pageId, blockId }: { pageId: string; blockId: string }) => {
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.delete', blockId, pageId,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { ok: true };
    },

    // ── Write: generic per-property editor (individual + batch) ─────────────
    //
    // Translates a flat property bag into the right sequence of canonical
    // actions. Supports geometry (x/y/width/height/rotation/pageLayer),
    // text properties (content, fontSize, fontFamily, color, bold, italic,
    // underline, align, agentId, frame), latex (latex, focusMode), and a
    // raw `textStyle` passthrough for anything else the renderer reads.

    updateBlock: async ({ pageId, blockId, properties }: {
      pageId: string;
      blockId: string;
      properties: Record<string, unknown>;
    }) => {
      this._ensureCurrentPage(pageId);
      const applied = this._applyBlockProperties(pageId, blockId, properties);
      return { ok: true, applied };
    },

    updateBlocks: async ({ updates }: {
      updates: Array<{
        pageId: string;
        blockId: string;
        properties: Record<string, unknown>;
      }>;
    }) => {
      const results: Array<{ blockId: string; applied: string[] }> = [];
      for (const u of updates) {
        this._ensureCurrentPage(u.pageId);
        const applied = this._applyBlockProperties(u.pageId, u.blockId, u.properties);
        results.push({ blockId: u.blockId, applied });
      }
      return { ok: true, count: results.length, results };
    },

    // ── Write: figures (basic shapes) ───────────────────────────────────────

    createShape: async ({ pageId, shape, bbox, penSize, lineStyle }: {
      pageId: string;
      shape: 'rect' | 'ellipse' | 'line';
      bbox: { x: number; y: number; width: number; height: number };
      penSize?: number;
      lineStyle?: 'solid' | 'dashed' | 'dotted';
    }) => {
      const blockId = `api_fig_${newId()}`;
      this._ensureCurrentPage(pageId);
      const common = {
        type: 'figure.create' as const,
        blockId, pageId, bbox,
        replacedStrokeIds: [],
        penSize: penSize ?? 2,
        lineStyle: lineStyle ?? 'solid',
        origin: 'api' as const,
        timestamp: ts(),
      };
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;

      if (shape === 'rect') {
        this._dispatch({
          ...common,
          shapeKind: 'rectangle',
          vertices: makeRectVertices(bbox),
          edges: makeRectEdges(),
        } as GrimoireAction);
      } else if (shape === 'ellipse') {
        this._dispatch({
          ...common,
          shapeKind: 'ellipse',
          vertices: [],
          edges: [],
          center: { x: cx, y: cy },
          rx: bbox.width / 2,
          ry: bbox.height / 2,
        } as GrimoireAction);
      } else if (shape === 'line') {
        this._dispatch({
          ...common,
          shapeKind: 'line',
          vertices: [
            { id: 'v0', x: bbox.x, y: bbox.y },
            { id: 'v1', x: bbox.x + bbox.width, y: bbox.y + bbox.height },
          ],
          edges: [{ id: 'e0', from: 'v0', to: 'v1', type: 'line' }],
        } as GrimoireAction);
      } else {
        throw new Error('unsupported shape: ' + shape);
      }
      return { blockId };
    },

    // ── Write: strokes ──────────────────────────────────────────────────────

    addStroke: async ({ pageId, points, pen, color, size, opacity, style, pageLayer, groupId }: {
      pageId: string;
      points: Array<{ x: number; y: number; pressure?: number; tiltX?: number; tiltY?: number; twist?: number; timestamp?: number }>;
      pen?: PenStyle;
      color?: string;
      size?: number;
      opacity?: number;
      style?: Partial<InkStyle>;
      pageLayer?: number;
      /** Optional: add to a bridge-side group. */
      groupId?: string;
    }) => {
      if (!points || points.length < 2) throw new Error('addStroke: need at least 2 points');
      const strokeId = `api_ink_${newId()}`;
      const t = ts();
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'ink.commit',
        strokeId,
        pageId,
        points: toStrokePoints(points, t),
        style: makeInkStyle({ pen, color, size, opacity, style }),
        pageLayer,
        origin: 'api',
        timestamp: t,
      } as GrimoireAction);
      if (groupId) this._addStrokeToGroup(groupId, pageId, strokeId);
      return { strokeId };
    },

    addStrokes: async ({ pageId, strokes, pen, color, size, opacity, pageLayer, groupId }: {
      pageId: string;
      strokes: Array<{
        points: Array<{ x: number; y: number; pressure?: number; tiltX?: number; tiltY?: number; twist?: number; timestamp?: number }>;
        pen?: PenStyle;
        color?: string;
        size?: number;
        opacity?: number;
        style?: Partial<InkStyle>;
        pageLayer?: number;
      }>;
      pen?: PenStyle;
      color?: string;
      size?: number;
      opacity?: number;
      pageLayer?: number;
      /** Optional: tie all created strokes into a bridge-side group. */
      groupId?: string;
    }) => {
      if (!strokes || !strokes.length) throw new Error('addStrokes: empty strokes array');
      this._ensureCurrentPage(pageId);
      const ids: string[] = [];
      const t0 = ts();
      for (let i = 0; i < strokes.length; i++) {
        const s = strokes[i];
        if (!s.points || s.points.length < 2) continue;
        const strokeId = `api_ink_${newId()}`;
        this._dispatch({
          type: 'ink.commit',
          strokeId,
          pageId,
          points: toStrokePoints(s.points, t0 + i * 100),
          style: makeInkStyle({
            pen: s.pen ?? pen,
            color: s.color ?? color,
            size: s.size ?? size,
            opacity: s.opacity ?? opacity,
            style: s.style,
          }),
          pageLayer: s.pageLayer ?? pageLayer,
          origin: 'api',
          timestamp: t0 + i,
        } as GrimoireAction);
        ids.push(strokeId);
        if (groupId) this._addStrokeToGroup(groupId, pageId, strokeId);
      }
      return { strokeIds: ids };
    },

    // ── Write: spline / polyline figures ────────────────────────────────────

    addSpline: async ({ pageId, waypoints, penSize, lineStyle, pageLayer }: {
      pageId: string;
      waypoints: Array<{ x: number; y: number }>;
      penSize?: number;
      lineStyle?: 'solid' | 'dashed' | 'dotted';
      pageLayer?: number;
    }) => {
      if (!waypoints || waypoints.length < 2) throw new Error('addSpline: need >= 2 waypoints');
      this._ensureCurrentPage(pageId);
      const blockId = `api_fig_${newId()}`;
      const vertices = waypoints.map((p, i) => ({ id: `v${i}`, x: p.x, y: p.y }));
      const edges = splineEdges(waypoints);
      this._dispatch({
        type: 'figure.create',
        blockId, pageId,
        shapeKind: 'spline',
        vertices, edges,
        bbox: bboxOf(waypoints),
        replacedStrokeIds: [],
        penSize: penSize ?? 2,
        lineStyle: lineStyle ?? 'solid',
        pageLayer,
        origin: 'api',
        timestamp: ts(),
      } as GrimoireAction);
      return { blockId };
    },

    addPolyline: async ({ pageId, points, closed, penSize, lineStyle, pageLayer }: {
      pageId: string;
      points: Array<{ x: number; y: number }>;
      closed?: boolean;
      penSize?: number;
      lineStyle?: 'solid' | 'dashed' | 'dotted';
      pageLayer?: number;
    }) => {
      if (!points || points.length < 2) throw new Error('addPolyline: need >= 2 points');
      this._ensureCurrentPage(pageId);
      const blockId = `api_fig_${newId()}`;
      const vertices = points.map((p, i) => ({ id: `v${i}`, x: p.x, y: p.y }));
      const edges: Array<{ id: string; from: string; to: string; type: string }> = [];
      for (let i = 0; i < points.length - 1; i++) {
        edges.push({ id: `e${i}`, from: `v${i}`, to: `v${i + 1}`, type: 'line' });
      }
      if (closed && points.length > 2) {
        edges.push({ id: `e${points.length - 1}`, from: `v${points.length - 1}`, to: 'v0', type: 'line' });
      }
      this._dispatch({
        type: 'figure.create',
        blockId, pageId,
        shapeKind: closed ? 'polygon' : 'polyline',
        vertices, edges,
        bbox: bboxOf(points),
        replacedStrokeIds: [],
        penSize: penSize ?? 2,
        lineStyle: lineStyle ?? 'solid',
        pageLayer,
        origin: 'api',
        timestamp: ts(),
      } as GrimoireAction);
      return { blockId };
    },

    // ── Batch / JSON I/O ────────────────────────────────────────────────────

    batch: async ({ actions }: { actions: GrimoireAction[] }) => {
      if (!actions || !actions.length) return { dispatched: 0 };
      let count = 0;
      for (const a of actions) {
        try {
          // Ensure origin/timestamp are set
          const filled = { origin: 'api', timestamp: ts(), ...a } as GrimoireAction;
          this._dispatch(filled);
          count++;
        } catch (e) {
          console.warn('[grimoire-bridge] batch action failed', e, a);
        }
      }
      return { dispatched: count };
    },

    /** Dump a page as a JSON snapshot (index + blocks + arrows + strokes).
     *  Useful for offline save / export / diff. */
    exportPage: async ({ pageId, strokes }: {
      pageId: string;
      strokes?: 'none' | 'polyline' | 'knots';
    }) => {
      return await (this.api as unknown as {
        getPage: (p: { pageId: string; strokes?: string; strokeSamples?: number }) => Promise<unknown>;
      }).getPage({ pageId, strokes: strokes ?? 'knots' });
    },

    // ── Layer control ───────────────────────────────────────────────────────

    setLayer: async ({ pageId, blockId, pageLayer }: {
      pageId: string; blockId: string; pageLayer: number;
    }) => {
      const clamped = Math.max(0, Math.min(5, Math.round(pageLayer)));
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.setLayer', blockId, pageId, pageLayer: clamped,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { pageLayer: clamped };
    },

    /** Raise or lower a block's pageLayer by `delta` (default ±1), clamped 0–5. */
    nudgeLayer: async ({ pageId, blockId, delta }: {
      pageId: string; blockId: string; delta: number;
    }) => {
      const block = this._findBlock(pageId, blockId);
      if (!block) throw new Error('nudgeLayer: block not found: ' + blockId);
      const current = (block.pageLayer as number | undefined) ?? 1;
      const next = Math.max(0, Math.min(5, current + delta));
      this._ensureCurrentPage(pageId);
      this._dispatch({
        type: 'block.setLayer', blockId, pageId, pageLayer: next,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { pageLayer: next, previous: current };
    },

    // ── Groups (bridge-side, ephemeral) ─────────────────────────────────────

    /** Create a new group binding existing block/stroke ids on a page.
     *  Groups live in bridge memory and don't survive a reload — use them for
     *  within-session composition, transforms, and copy. */
    createGroup: async ({ pageId, blockIds, strokeIds, groupId }: {
      pageId: string;
      blockIds?: string[];
      strokeIds?: string[];
      groupId?: string;
    }) => {
      const id = groupId ?? `grp_${newId()}`;
      if (this._groups.has(id)) throw new Error('group exists: ' + id);
      this._groups.set(id, {
        id, pageId,
        blockIds: new Set(blockIds ?? []),
        strokeIds: new Set(strokeIds ?? []),
        createdAt: ts(),
      });
      // Log the canonical action even though the host is a no-op for it —
      // keeps the action log honest and lets a future host handler pick it up.
      if (blockIds && blockIds.length) {
        this._dispatch({
          type: 'block.group',
          pageId, blockIds,
          newGroupId: id,
          origin: 'api',
          timestamp: ts(),
        } as GrimoireAction);
      }
      return { groupId: id, blockCount: (blockIds ?? []).length, strokeCount: (strokeIds ?? []).length };
    },

    groupMembers: async ({ groupId }: { groupId: string }) => {
      const g = this._groups.get(groupId);
      if (!g) throw new Error('no such group: ' + groupId);
      return {
        groupId: g.id, pageId: g.pageId,
        blockIds: [...g.blockIds], strokeIds: [...g.strokeIds],
      };
    },

    listGroups: async ({ pageId }: { pageId?: string } = {}) => {
      const out: Array<{ groupId: string; pageId: string; blockCount: number; strokeCount: number }> = [];
      for (const g of this._groups.values()) {
        if (pageId && g.pageId !== pageId) continue;
        out.push({
          groupId: g.id, pageId: g.pageId,
          blockCount: g.blockIds.size, strokeCount: g.strokeIds.size,
        });
      }
      return out;
    },

    /** Translate every member by (dx, dy). Blocks via block.move, strokes via
     *  strokes.transform. No-op for members that have been removed from state. */
    moveGroup: async ({ groupId, dx, dy }: { groupId: string; dx: number; dy: number }) => {
      const g = this._groups.get(groupId);
      if (!g) throw new Error('no such group: ' + groupId);
      this._ensureCurrentPage(g.pageId);
      for (const blockId of g.blockIds) {
        this._dispatch({
          type: 'block.move', blockId, pageId: g.pageId, dx, dy,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
      if (g.strokeIds.size) {
        const h = this._host as unknown as {
          _onWebviewMessage(m: { type: string; [k: string]: unknown }): void;
        };
        h._onWebviewMessage({
          type: 'strokes.transform',
          pageId: g.pageId,
          strokeIds: [...g.strokeIds],
          transform: { kind: 'move', dx, dy },
        });
      }
      return { movedBlocks: g.blockIds.size, movedStrokes: g.strokeIds.size };
    },

    /** Set pageLayer for every block in the group (strokes keep their own layer). */
    setLayerGroup: async ({ groupId, pageLayer }: { groupId: string; pageLayer: number }) => {
      const g = this._groups.get(groupId);
      if (!g) throw new Error('no such group: ' + groupId);
      const clamped = Math.max(0, Math.min(5, Math.round(pageLayer)));
      this._ensureCurrentPage(g.pageId);
      for (const blockId of g.blockIds) {
        this._dispatch({
          type: 'block.setLayer', blockId, pageId: g.pageId, pageLayer: clamped,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
      return { affected: g.blockIds.size, pageLayer: clamped };
    },

    /** Delete the group. If cascade, also deletes every member from the page. */
    deleteGroup: async ({ groupId, cascade }: { groupId: string; cascade?: boolean }) => {
      const g = this._groups.get(groupId);
      if (!g) throw new Error('no such group: ' + groupId);
      let removedBlocks = 0, removedStrokes = 0;
      if (cascade) this._ensureCurrentPage(g.pageId);
      if (cascade) {
        for (const blockId of g.blockIds) {
          this._dispatch({
            type: 'block.delete', blockId, pageId: g.pageId,
            origin: 'api', timestamp: ts(),
          } as GrimoireAction);
          removedBlocks++;
        }
        for (const strokeId of g.strokeIds) {
          this._dispatch({
            type: 'ink.erase', pageId: g.pageId, target: strokeId,
            origin: 'api', timestamp: ts(),
          } as GrimoireAction);
          removedStrokes++;
        }
      }
      this._groups.delete(groupId);
      return { removedBlocks, removedStrokes, cascade: !!cascade };
    },

    // ── Duplicate (copy at N target locations) ──────────────────────────────

    /** Clone a block, stroke, or group at each target offset.  Each target is
     *  a {dx, dy} offset applied to the source's position.  Returns the list
     *  of new ids per target (one element for block/stroke, group snapshot
     *  for groups). Great for mosaics, patterns, and motif reproduction. */
    duplicate: async ({ pageId, source, targets, groupId }: {
      pageId: string;
      /** blockId | strokeId | groupId */
      source: string;
      /** Translation offsets. One clone per entry. */
      targets: Array<{ dx: number; dy: number }>;
      /** If source is a group: optional groupId for each new clone-group.
       *  Default auto-generated per target. */
      groupId?: string;
    }) => {
      if (!targets || !targets.length) throw new Error('duplicate: targets array empty');
      this._ensureCurrentPage(pageId);
      const kind = this._classifySource(pageId, source);
      const out: Array<Record<string, unknown>> = [];
      for (let i = 0; i < targets.length; i++) {
        const { dx, dy } = targets[i];
        if (kind === 'block') {
          const newId = this._cloneBlock(pageId, source, dx, dy);
          if (newId) out.push({ blockId: newId });
        } else if (kind === 'stroke') {
          const newId = this._cloneStroke(pageId, source, dx, dy);
          if (newId) out.push({ strokeId: newId });
        } else if (kind === 'group') {
          const newGroup = this._cloneGroup(source, dx, dy, groupId ? `${groupId}_${i}` : undefined);
          if (newGroup) out.push(newGroup);
        } else {
          throw new Error('duplicate: unknown source id (not a block, stroke, or group): ' + source);
        }
      }
      return { kind, clones: out };
    },

    // ── Write: pages & navigation ───────────────────────────────────────────

    createPage: async ({ title, afterIndex, sectionId, color }: {
      title: string; afterIndex?: number; sectionId?: string; color?: string;
    }) => {
      const h = this._host as unknown as { _pages: Array<{ id: string }>; _currentPageIndex: number };
      const pageId = `page_${newId()}`;
      const idx = afterIndex ?? h._currentPageIndex;
      this._dispatch({
        type: 'page.create', pageId, insertAfterIndex: idx,
        title, color, sectionId,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { pageId };
    },

    gotoPage: async ({ pageId }: { pageId: string }) => {
      this._dispatch({
        type: 'page.goto', pageId,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { ok: true };
    },

    turnPage: async ({ direction }: { direction: 'forward' | 'back' }) => {
      this._dispatch({
        type: 'page.turn', direction,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      return { ok: true };
    },
  };

  // ── Internal ──────────────────────────────────────────────────────────────

  private _dispatch(action: GrimoireAction): void {
    const h = this._host as unknown as {
      _onWebviewMessage(m: { type: string; payload?: unknown }): void;
    };
    h._onWebviewMessage({ type: 'action', payload: action });
  }

  /** Ensure the webview's currentPageIndex matches pageId.  Necessary because
   *  the app's notebook_state resolves block ids via currentPage() only — so
   *  text.update/block.move/block.setLayer/etc. silently drop when the webview
   *  hasn't caught up to the targeted page (e.g. right after createPage, or
   *  operating on a non-current page).
   *
   *  Checks the WEBVIEW's _currentPageIndex, NOT the host's — the host may
   *  have advanced its pointer (e.g. from page.create) without the webview
   *  following. */
  private _ensureCurrentPage(pageId: string): void {
    const app = this._getApp() as unknown as { _state?: {
      _pageOrder: string[]; _currentPageIndex: number;
    } };
    const state = app?._state;
    if (state) {
      const current = state._pageOrder[state._currentPageIndex];
      if (current === pageId) return;
    }
    this._dispatch({
      type: 'page.goto', pageId,
      origin: 'api', timestamp: ts(),
    } as GrimoireAction);
  }

  // ── Host state accessors ──────────────────────────────────────────────────

  private _findPage(pageId: string): Record<string, unknown> | undefined {
    const h = this._host as unknown as { _pages: Array<Record<string, unknown>> };
    return h._pages.find(p => p.id === pageId);
  }

  private _findBlock(pageId: string, blockId: string): Record<string, unknown> | undefined {
    const page = this._findPage(pageId);
    if (!page) return undefined;
    return (page.blocks as Map<string, Record<string, unknown>>).get(blockId);
  }

  /** Translate a flat property bag to canonical actions.
   *  Returns the list of property names that were applied (useful for the
   *  caller to see what the bridge actually recognised). */
  private _applyBlockProperties(
    pageId: string, blockId: string,
    props: Record<string, unknown>,
  ): string[] {
    const applied: string[] = [];
    const block = this._findBlock(pageId, blockId) as
      (Record<string, unknown> & { kind?: string; bbox?: { x: number; y: number; width: number; height: number }; rotation?: number }) | undefined;
    if (!block || !block.bbox) return applied;
    const kind = block.kind;
    const cur = block.bbox;

    // ── Geometry: absolute x/y → relative move ─────────────────────────────
    let dx = 0, dy = 0;
    if (typeof props.x === 'number') { dx = (props.x as number) - cur.x; applied.push('x'); }
    if (typeof props.y === 'number') { dy = (props.y as number) - cur.y; applied.push('y'); }
    if (typeof props.dx === 'number') { dx += props.dx as number; applied.push('dx'); }
    if (typeof props.dy === 'number') { dy += props.dy as number; applied.push('dy'); }
    if (dx !== 0 || dy !== 0) {
      this._dispatch({
        type: 'block.move', blockId, pageId, dx, dy,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
    }

    // ── Geometry: width/height → scale from block's top-left ───────────────
    const newW = typeof props.width  === 'number' ? props.width  as number : cur.width;
    const newH = typeof props.height === 'number' ? props.height as number : cur.height;
    if (newW !== cur.width || newH !== cur.height) {
      const sx = cur.width  > 0 ? newW / cur.width  : 1;
      const sy = cur.height > 0 ? newH / cur.height : 1;
      this._dispatch({
        type: 'block.scale', blockId, pageId,
        pivot: { x: cur.x, y: cur.y }, sx, sy,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      if (typeof props.width  === 'number') applied.push('width');
      if (typeof props.height === 'number') applied.push('height');
    }

    // ── pageLayer ──────────────────────────────────────────────────────────
    if (typeof props.pageLayer === 'number') {
      this._dispatch({
        type: 'block.setLayer', blockId, pageId,
        pageLayer: Math.max(0, Math.min(5, Math.round(props.pageLayer as number))),
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      applied.push('pageLayer');
    }

    // ── rotation (absolute radians) ────────────────────────────────────────
    if (typeof props.rotation === 'number') {
      const cx = cur.x + cur.width / 2;
      const cy = cur.y + cur.height / 2;
      const dAngle = (props.rotation as number) - (block.rotation ?? 0);
      if (dAngle !== 0) {
        this._dispatch({
          type: 'block.rotate', blockId, pageId,
          pivot: { x: cx, y: cy }, dAngle,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
      applied.push('rotation');
    }

    // ── Text content ───────────────────────────────────────────────────────
    if (kind === 'text' && typeof props.content === 'string') {
      this._dispatch({
        type: 'text.update', blockId, pageId, content: props.content as string,
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
      applied.push('content');
    }

    // ── Text style (bundled into a single text.style dispatch) ─────────────
    if (kind === 'text') {
      const style: Record<string, unknown> = {};
      const styleKeys = [
        'fontSize', 'fontFamily', 'color', 'bold', 'italic',
        'underline', 'align', 'agentId',
      ];
      for (const k of styleKeys) {
        if (props[k] !== undefined) { style[k] = props[k]; applied.push(k); }
      }
      if (typeof props.frame === 'boolean') {
        style.showFrame = props.frame;
        applied.push('frame');
      }
      if (props.textStyle && typeof props.textStyle === 'object') {
        Object.assign(style, props.textStyle as Record<string, unknown>);
        applied.push('textStyle');
      }
      if (Object.keys(style).length > 0) {
        this._dispatch({
          type: 'text.style', blockId, pageId, style,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
    }

    // ── LaTeX ──────────────────────────────────────────────────────────────
    if (kind === 'latex') {
      if (typeof props.latex === 'string') {
        this._dispatch({
          type: 'latex.confirm', blockId, pageId, latex: props.latex as string,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
        applied.push('latex');
      }
      if (typeof props.focusMode === 'boolean') {
        this._dispatch({
          type: 'latex.focus', blockId, pageId, focus: props.focusMode as boolean,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
        applied.push('focusMode');
      }
    }

    return applied;
  }

  private _findStroke(pageId: string, strokeId: string): {
    id: string;
    points: StrokePoint[];
    style: Record<string, unknown>;
    pageLayer?: number;
  } | undefined {
    const h = this._host as unknown as {
      _pageStrokes: Map<string, Map<string, {
        id: string; points: StrokePoint[]; style: Record<string, unknown>;
        timestamp: number; pageLayer?: number;
      }>>;
    };
    return h._pageStrokes.get(pageId)?.get(strokeId);
  }

  private _classifySource(pageId: string, sourceId: string): 'block' | 'stroke' | 'group' | 'unknown' {
    if (this._groups.has(sourceId)) return 'group';
    if (this._findBlock(pageId, sourceId)) return 'block';
    if (this._findStroke(pageId, sourceId)) return 'stroke';
    return 'unknown';
  }

  // ── Group membership helpers ──────────────────────────────────────────────

  private _addBlockToGroup(groupId: string, pageId: string, blockId: string): void {
    let g = this._groups.get(groupId);
    if (!g) {
      g = { id: groupId, pageId, blockIds: new Set(), strokeIds: new Set(), createdAt: ts() };
      this._groups.set(groupId, g);
    }
    if (g.pageId !== pageId) {
      console.warn('[grimoire-bridge] group', groupId, 'is on a different page; ignoring add');
      return;
    }
    g.blockIds.add(blockId);
  }

  private _addStrokeToGroup(groupId: string, pageId: string, strokeId: string): void {
    let g = this._groups.get(groupId);
    if (!g) {
      g = { id: groupId, pageId, blockIds: new Set(), strokeIds: new Set(), createdAt: ts() };
      this._groups.set(groupId, g);
    }
    if (g.pageId !== pageId) {
      console.warn('[grimoire-bridge] group', groupId, 'is on a different page; ignoring add');
      return;
    }
    g.strokeIds.add(strokeId);
  }

  // ── Clone (per-kind) ──────────────────────────────────────────────────────

  private _cloneBlock(pageId: string, blockId: string, dx: number, dy: number): string | null {
    const src = this._findBlock(pageId, blockId);
    if (!src) return null;
    const newBlockId = `api_${src.kind ?? 'blk'}_${newId()}`;
    const srcBbox = src.bbox as { x: number; y: number; width: number; height: number };
    const bbox = { x: srcBbox.x + dx, y: srcBbox.y + dy, width: srcBbox.width, height: srcBbox.height };

    this._ensureCurrentPage(pageId);
    // 1) block.create — always first
    this._dispatch({
      type: 'block.create',
      blockId: newBlockId,
      pageId,
      kind: src.kind as string,
      bbox,
      pageLayer: src.pageLayer as number | undefined,
      origin: 'api',
      timestamp: ts(),
    } as GrimoireAction);

    // 2) per-kind detail actions
    if (src.kind === 'text') {
      const content = src.content as string | undefined;
      if (content) {
        this._dispatch({
          type: 'text.update', blockId: newBlockId, pageId, content,
          origin: 'api', timestamp: ts(),
        } as GrimoireAction);
      }
      const style = (src.textStyle as Record<string, unknown> | undefined) ?? {};
      const fontSize = (src.fontSize as number | undefined) ?? (style.fontSize as number | undefined) ?? 16;
      this._dispatch({
        type: 'text.style', blockId: newBlockId, pageId,
        style: { ...style, fontSize },
        origin: 'api', timestamp: ts(),
      } as GrimoireAction);
    } else if (src.kind === 'figure') {
      // Translate the figure's vertices + edge control points. figure.create
      // is the only shot — we re-emit it instead of relying on block.create.
      const srcVerts = (src.vertices as Array<{ id: string; x: number; y: number }> | undefined) ?? [];
      const srcEdges = (src.edges as Array<{ id: string; from: string; to: string; type: string; controlPoints?: XY[] }> | undefined) ?? [];
      const verts = srcVerts.map(v => ({ id: v.id, x: v.x + dx, y: v.y + dy }));
      const edges = srcEdges.map(e => ({
        ...e,
        controlPoints: e.controlPoints ? e.controlPoints.map(cp => ({ x: cp.x + dx, y: cp.y + dy })) : undefined,
      }));
      const cpAction: Record<string, unknown> = {
        type: 'figure.create',
        blockId: newBlockId,
        pageId,
        shapeKind: src.shapeKind,
        vertices: verts,
        edges,
        bbox,
        replacedStrokeIds: [],
        penSize: src.penSize,
        lineStyle: src.lineStyle,
        origin: 'api',
        timestamp: ts(),
      };
      const center = src.center as { x: number; y: number } | undefined;
      if (center) cpAction.center = { x: center.x + dx, y: center.y + dy };
      if (src.rx !== undefined) cpAction.rx = src.rx;
      if (src.ry !== undefined) cpAction.ry = src.ry;
      this._dispatch(cpAction as unknown as GrimoireAction);
    }
    // arcane/code/latex blocks: block.create carries the kind; content-specific
    // fields (language, fileRef, arcaneType, arcaneState) aren't cloned here —
    // we'd need dedicated action types. Skip for v1.
    return newBlockId;
  }

  private _cloneStroke(pageId: string, strokeId: string, dx: number, dy: number): string | null {
    const src = this._findStroke(pageId, strokeId);
    if (!src) return null;
    const newStrokeId = `api_ink_${newId()}`;
    const t = ts();
    this._ensureCurrentPage(pageId);
    const translated: StrokePoint[] = src.points.map((p, i) => ({
      x: p.x + dx, y: p.y + dy,
      pressure: p.pressure, tiltX: p.tiltX, tiltY: p.tiltY, twist: p.twist,
      timestamp: t + i,
    }));
    this._dispatch({
      type: 'ink.commit',
      strokeId: newStrokeId,
      pageId,
      points: translated,
      style: src.style as unknown as InkStyle,
      pageLayer: src.pageLayer,
      origin: 'api',
      timestamp: t,
    } as GrimoireAction);
    return newStrokeId;
  }

  private _cloneGroup(groupId: string, dx: number, dy: number, newGroupId?: string): {
    groupId: string; blockIds: string[]; strokeIds: string[];
  } | null {
    const src = this._groups.get(groupId);
    if (!src) return null;
    const ng: GroupEntry = {
      id: newGroupId ?? `grp_${newId()}`,
      pageId: src.pageId,
      blockIds: new Set(),
      strokeIds: new Set(),
      createdAt: ts(),
    };
    for (const bId of src.blockIds) {
      const cloned = this._cloneBlock(src.pageId, bId, dx, dy);
      if (cloned) ng.blockIds.add(cloned);
    }
    for (const sId of src.strokeIds) {
      const cloned = this._cloneStroke(src.pageId, sId, dx, dy);
      if (cloned) ng.strokeIds.add(cloned);
    }
    this._groups.set(ng.id, ng);
    return { groupId: ng.id, blockIds: [...ng.blockIds], strokeIds: [...ng.strokeIds] };
  }
}
